import styled from "@emotion/styled";
import { isEmpty } from "lodash";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { FieldArray } from "react-final-form-arrays";

import { Add, Info } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { AutomationType } from "routes/automations/constants";
import { OpenedAccordionIndex } from "routes/automations/workflows/interfaces";
import { withVar } from "themes/helpers";

import { ActionPicker } from "./ActionPicker";
import { ActionFieldArrayElement } from "./interfaces";
import { ACTION_EMPTY_STATE_TEXT } from "./utils";

interface IProps {
  /**
   * Ref object for the action pickers. Used to scroll to the first error on
   * submit.
   */
  actionPickerRefs?: React.RefObject<Array<HTMLDivElement | null>>;
  limitReached: boolean;
  openedAccordionIndex: OpenedAccordionIndex;
  setOpenedAccordionIndex: Dispatch<SetStateAction<OpenedAccordionIndex>>;
  automationType: AutomationType;
}

export const ActionPickerList: FC<IProps> = ({
  actionPickerRefs,
  limitReached,
  openedAccordionIndex,
  setOpenedAccordionIndex,
  automationType,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <FieldArray<ActionFieldArrayElement> name="actions">
        {({ fields, meta }) => {
          const hasOnlyEmptyActions = fields.value
            ? fields.value.filter(action => !isEmpty(action)).length === 0
            : true;

          const shouldShowError = meta.submitFailed && hasOnlyEmptyActions;

          const handleModalOpen = (): void => {
            fields.push({});
            setModalOpen(true);
          };

          const handleModalClose = (): void => {
            setModalOpen(false);
          };

          return (
            <Flex column marginBottom={withVar(SpacingToken.Spacing5)}>
              {fields.length === 0 && <EmptyStateText size="sm">{ACTION_EMPTY_STATE_TEXT}</EmptyStateText>}
              {fields.map((name, idx) => (
                <div
                  key={name}
                  ref={el => {
                    if (actionPickerRefs?.current) {
                      actionPickerRefs.current[idx] = el;
                    }
                  }}
                >
                  <ActionPicker
                    index={idx}
                    fields={fields}
                    limitReached={limitReached}
                    onModalClose={() => handleModalClose()}
                    isModalOpen={modalOpen}
                    openedIndex={openedAccordionIndex}
                    setOpenedIndex={setOpenedAccordionIndex}
                    automationType={automationType}
                  />
                  {!isEmpty(fields.value[idx]) && <Divider />}
                </div>
              ))}
              <Flex width="100%">
                <AddNewAction
                  kind="high-contrast"
                  onClick={handleModalOpen}
                  size="compact"
                  disabled={limitReached}
                  icon={<Icon source={Add} />}
                  aligned={hasOnlyEmptyActions}
                  data-testid="action-picker"
                />
                {shouldShowError && (
                  <Flex
                    alignItems="center"
                    gap={withVar(SpacingToken.Spacing1)}
                    marginLeft={withVar(SpacingToken.Spacing3)}
                  >
                    <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicNegative)} />
                    <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                      At least one action must be added.
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          );
        }}
      </FieldArray>
    </>
  );
};

const Divider = styled.div`
  border-right: 1px dashed ${withVar(DesignToken.BorderBasicPrimary)};
  min-height: var(${SpacingToken.Spacing4});
  width: var(${SpacingToken.Spacing7});
`;

const EmptyStateText = styled(Text)`
  margin-bottom: var(${SpacingToken.Spacing3});
  color: var(${DesignToken.ContentBasicDisabled});
`;

const AddNewAction = styled(Button)<{ aligned: boolean }>`
  margin-left: ${({ aligned }): string => (aligned ? "0" : withVar(SpacingToken.Spacing3))};
`;
